/**
 * Subscription Utilities
 * Helper functions for subscription management and validation
 */

export interface Subscription {
  id: string;
  shopId: string;
  planId: string;
  planName: string;
  planStartDate: string;
  planEndDate: string;
  isActive: boolean;
  autoRenew: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionStatus {
  hasActivePlan: boolean;
  planName: string | null;
  expiryDate: Date | null;
  daysRemaining: number;
  isExpired: boolean;
  isExpiringSoon: boolean; // Less than 7 days
}

/**
 * Get the subscription status from a list of subscriptions
 * Returns the most relevant active subscription or expired status
 */
export const getSubscriptionStatus = (subscriptions?: Subscription[]): SubscriptionStatus => {
  if (!subscriptions || subscriptions.length === 0) {
    return {
      hasActivePlan: false,
      planName: null,
      expiryDate: null,
      daysRemaining: 0,
      isExpired: true,
      isExpiringSoon: false,
    };
  }

  // Find the active subscription with the latest expiry date
  const activeSubscription = subscriptions
    .filter(sub => sub.isActive)
    .sort((a, b) => new Date(b.planEndDate).getTime() - new Date(a.planEndDate).getTime())[0];

  if (!activeSubscription) {
    return {
      hasActivePlan: false,
      planName: null,
      expiryDate: null,
      daysRemaining: 0,
      isExpired: true,
      isExpiringSoon: false,
    };
  }

  const expiryDate = new Date(activeSubscription.planEndDate);
  const today = new Date();
  const daysRemaining = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isExpired = daysRemaining <= 0;
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 7;

  return {
    hasActivePlan: !isExpired,
    planName: activeSubscription.planName,
    expiryDate,
    daysRemaining: Math.max(0, daysRemaining),
    isExpired,
    isExpiringSoon,
  };
};

/**
 * Format date for display
 */
export const formatExpiryDate = (date: Date | null): string => {
  if (!date) return '';

  const day = date.getDate();
  const month = date.toLocaleDateString('en-IN', { month: 'short' });
  const year = date.getFullYear();

  // Get ordinal suffix (st, nd, rd, th)
  const getSuffix = (d: number) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${getSuffix(day)} ${month} ${year}`;
};

